import React, { useState } from "react";
import {
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as FileCopyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import PageContainer from "../components/common/PageContainer";
import ResponsiveCard from "../components/common/ResponsiveCard";
import SearchInput from "../components/common/SearchInput";
import ConfirmationDialog from "../components/common/ConfirmationDialog";
import { useNavigate } from "react-router-dom";

const Checklists = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Mock data for checklists
  const allChecklists = [
    {
      id: 1,
      title: "Daily Equipment Inspection",
      status: "in_progress",
      items: 10,
      completed: 5,
      dueDate: "2025-09-05",
    },
    {
      id: 2,
      title: "Weekly Maintenance Checklist",
      status: "completed",
      items: 15,
      completed: 15,
      dueDate: "2025-08-28",
    },
    {
      id: 3,
      title: "Safety Compliance Review",
      status: "completed",
      items: 8,
      completed: 8,
      dueDate: "2025-08-25",
    },
    {
      id: 4,
      title: "Monthly System Audit",
      status: "not_started",
      items: 20,
      completed: 0,
      dueDate: "2025-09-15",
    },
    {
      id: 5,
      title: "Quarterly Compliance Check",
      status: "in_progress",
      items: 12,
      completed: 3,
      dueDate: "2025-09-30",
    },
  ];

  // Filter checklists based on tab and search term
  const getFilteredChecklists = () => {
    let filtered = [...allChecklists];

    // Filter by tab
    if (tabValue === 1) {
      filtered = filtered.filter(
        (checklist) => checklist.status === "in_progress"
      );
    } else if (tabValue === 2) {
      filtered = filtered.filter(
        (checklist) => checklist.status === "completed"
      );
    } else if (tabValue === 3) {
      filtered = filtered.filter(
        (checklist) => checklist.status === "not_started"
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((checklist) =>
        checklist.title.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const filteredChecklists = getFilteredChecklists();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleMenuOpen = (event, checklist) => {
    setAnchorEl(event.currentTarget);
    setSelectedChecklist(checklist);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    navigate(`/checklists/${selectedChecklist.id}`);
    handleMenuClose();
  };

  const handleDelete = () => {
    setConfirmDialogOpen(true);
    handleMenuClose();
  };

  const handleDuplicate = () => {
    // In a real application, we would duplicate the checklist via the API
    console.log("Duplicate checklist:", selectedChecklist);

    // For now, navigate to create new checklist page
    // In a real app, we would pass the duplicated data or ID as a parameter
    navigate("/checklists/new");
    handleMenuClose();
  };

  const confirmDelete = () => {
    // In a real application, we would delete the checklist via the API
    console.log("Delete checklist:", selectedChecklist);

    // For now, just close the dialog
    // In a real app, we would remove the item from the list after successful deletion
    setConfirmDialogOpen(false);

    // Simulate successful deletion by showing a temporary message
    // In a real app, this would be handled by a notification system
    alert("Checklist deleted successfully");
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "completed":
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="Completed"
            color="success"
            size="small"
            variant="outlined"
          />
        );
      case "in_progress":
        return (
          <Chip
            icon={<ScheduleIcon />}
            label="In Progress"
            color="primary"
            size="small"
            variant="outlined"
          />
        );
      case "not_started":
        return (
          <Chip
            icon={<ErrorIcon />}
            label="Not Started"
            color="warning"
            size="small"
            variant="outlined"
          />
        );
      default:
        return null;
    }
  };

  return (
    <PageContainer
      title="Checklists"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/checklists/new")}
        >
          New Checklist
        </Button>
      }
    >
      <Box sx={{ mb: 3 }}>
        <SearchInput
          placeholder="Search checklists..."
          onSearch={handleSearch}
          showFilter={true}
          onFilterClick={() => console.log("Open filter dialog")}
        />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="checklist tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" />
          <Tab label="In Progress" />
          <Tab label="Completed" />
          <Tab label="Not Started" />
        </Tabs>
      </Box>

      {filteredChecklists.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No checklists found.
          </Typography>
        </Box>
      ) : (
        filteredChecklists.map((checklist) => (
          <ResponsiveCard
            key={checklist.id}
            sx={{ mb: 2 }}
            content={
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        fontWeight: 500,
                        cursor: "pointer",
                        "&:hover": { color: "primary.main" },
                      }}
                      onClick={() => navigate(`/checklists/${checklist.id}`)}
                    >
                      {checklist.title}
                    </Typography>

                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {getStatusChip(checklist.status)}
                      <Typography variant="body2" color="text.secondary">
                        {checklist.completed}/{checklist.items} items completed
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Due: {checklist.dueDate}
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    aria-label="more"
                    onClick={(e) => handleMenuOpen(e, checklist)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Box>
            }
          />
        ))
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <FileCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Checklist"
        message="Are you sure you want to delete this checklist? This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
      />
    </PageContainer>
  );
};

export default Checklists;
